import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import type { SaveState } from "./use-match-editor";

export interface SaveStatusProps {
  saveState: SaveState;
  lastSavedAt: number | null;
  onRetry: () => void;
}

// Autosave status for the Match editor (ADR-0008). Calm by design: steady state
// is silent, `Speichert…` shows while a save is in flight, `Gespeichert` briefly
// acknowledges success then auto-hides, and a failed save stays put with a retry
// affordance so edits can't be lost silently. No live-ticking "vor X Sekunden"
// clock (ADR-0008 amended) — it was noise, and its changing width shoved the
// mode toggle sideways. Rendered in a fixed-width slot so it pushes nothing.
export function SaveStatus({ saveState, lastSavedAt, onRetry }: SaveStatusProps) {
  const intl = useIntl();
  const [showSaved, setShowSaved] = useState(false);
  const prevSavedAt = useRef(lastSavedAt);
  useEffect(() => {
    if (lastSavedAt == null || lastSavedAt === prevSavedAt.current) return;
    prevSavedAt.current = lastSavedAt;
    setShowSaved(true);
    const t = setTimeout(() => setShowSaved(false), 2000);
    return () => clearTimeout(t);
  }, [lastSavedAt]);

  const base = "text-[10px] tracking-[0.2em]";

  if (saveState === "error") {
    return (
      <span className={`flex items-center gap-2 ${base} text-red-600`} aria-live="polite">
        {intl.formatMessage({ id: "match.save.failed", defaultMessage: "Speichern fehlgeschlagen" })}
        <button
          type="button"
          onClick={onRetry}
          className="rounded-xs underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
        >
          {intl.formatMessage({ id: "match.save.retry", defaultMessage: "Erneut versuchen" })}
        </button>
      </span>
    );
  }
  if (saveState === "saving") {
    return (
      <span className={`${base} text-ink/55`} aria-live="polite">
        {intl.formatMessage({ id: "match.save.saving", defaultMessage: "Speichert…" })}
      </span>
    );
  }
  if (showSaved) {
    return (
      <span className={`${base} text-ink/40`} aria-live="polite">
        {intl.formatMessage({ id: "match.save.saved", defaultMessage: "Gespeichert" })}
      </span>
    );
  }
  return null;
}
